import * as React from 'react';
import {useEffect, useState} from 'react';
import {graphql, useLazyLoadQuery, useMutation} from 'react-relay';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {ArrowLeft} from 'lucide-react';
import {OpsTalentEditPageQuery} from './__generated__/OpsTalentEditPageQuery.graphql';
import {OpsTalentEditPageCreateMutation} from './__generated__/OpsTalentEditPageCreateMutation.graphql';
import {OpsTalentEditPageUpdateMutation} from './__generated__/OpsTalentEditPageUpdateMutation.graphql';
import {OpsTalentEditPageDeleteMutation} from './__generated__/OpsTalentEditPageDeleteMutation.graphql';
import {dangerButton, ghostButton, inputClass, primaryButton} from './lib';

const QUERY = graphql`
    query OpsTalentEditPageQuery($id: ID!, $skip: Boolean!) {
        node(id: $id) @skip(if: $skip) {
            ... on TalentProfile {
                id
                name
                slug
                roles
                headline
                bio
                skills
                dayRate
                hourRate
                imageUrl
                availability
                published
                featured
                position
                portfolioLinks { label url }
                facebookProfileUrl
                facebookPageUrl
                facebookPageName
                facebookPageVerified
                facebookConnectPath
                user { name email }
            }
        }
    }`;

const CREATE = graphql`mutation OpsTalentEditPageCreateMutation($input: CreateTalentProfileInput!) { createTalentProfile(input: $input) { talentProfile { id } userErrors { message } } }`;
const UPDATE = graphql`mutation OpsTalentEditPageUpdateMutation($input: UpdateTalentProfileInput!) { updateTalentProfile(input: $input) { talentProfile { id } userErrors { message } } }`;
const DELETE = graphql`mutation OpsTalentEditPageDeleteMutation($input: DeleteTalentProfileInput!) { deleteTalentProfile(input: $input) { deletedId userErrors { message } } }`;

const ROLES = ['PHOTOGRAPHER', 'VIDEOGRAPHER', 'EDITOR', 'DIRECTOR', 'PRODUCER', 'COLORIST', 'SOUND_ENGINEER', 'DRONE_PILOT'] as const;
const AVAILABILITIES = ['AVAILABLE', 'BOOKED', 'UNAVAILABLE'] as const;

type Form = {
    name: string;
    slug: string;
    roles: string[];
    headline: string;
    bio: string;
    skills: string;        // comma-separated in the form
    dayRate: string;
    hourRate: string;
    imageUrl: string;
    availability: string;
    published: boolean;
    featured: boolean;
    position: string;
    portfolio: string;     // one per line: "Label | https://..." or just the URL
    facebookProfileUrl: string;
    facebookPageUrl: string;
};

const EMPTY: Form = {
    name: '', slug: '', roles: [], headline: '', bio: '', skills: '', dayRate: '', hourRate: '', imageUrl: '',
    availability: 'AVAILABLE', published: false, featured: false, position: '0', portfolio: '',
    facebookProfileUrl: '', facebookPageUrl: '',
};

const parseLinks = (text: string) =>
    text.split('\n').map((line) => line.trim()).filter(Boolean).map((line) => {
        const [a, b] = line.split('|').map((s) => s.trim());
        return b ? {label: a, url: b} : {label: null, url: a};
    });

const parseRate = (value: string): number | null => {
    if (value.trim() === '') return 0; // 0 clears the rate server-side
    const n = Number(value);
    return Number.isNaN(n) ? null : n;
};

type Existing = NonNullable<OpsTalentEditPageQuery['response']['node']>;

// Who (if anyone) has connected their Facebook sign-in, or the link to send them so they can.
const FacebookConnection: React.FC<{ profile: Existing; onDisconnect: () => void; busy: boolean }> = ({profile, onDisconnect, busy}) => {
    const [copied, setCopied] = useState(false);

    if (profile.user) {
        return (
            <p className="md:col-span-2 text-xs text-gray-400 flex items-center gap-3">
                Facebook sign-in connected: <span className="text-white">{profile.user.name ?? profile.user.email ?? 'unnamed'}</span>
                <button type="button" onClick={onDisconnect} disabled={busy} className={ghostButton}>Disconnect</button>
            </p>
        );
    }
    if (!profile.facebookConnectPath) return null;

    const url = `${window.location.origin}${profile.facebookConnectPath}`;
    const copy = async () => {
        await navigator.clipboard.writeText(url);
        setCopied(true);
    };
    return (
        <div className="md:col-span-2 text-xs text-gray-400">
            <p className="mb-1">Not connected. Send this link so they can sign in with Facebook and pick their Page (valid 30 days, one use):</p>
            <div className="flex items-center gap-2">
                <input readOnly value={url} onFocus={(e) => e.target.select()} className={`${inputClass} w-full font-mono text-xs`}/>
                <button type="button" onClick={copy} className={ghostButton}>{copied ? 'Copied' : 'Copy'}</button>
            </div>
        </div>
    );
};

// One form for creating and editing a crew profile.
const OpsTalentEditPage: React.FC = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const isNew = !id;
    const data = useLazyLoadQuery<OpsTalentEditPageQuery>(QUERY, {id: id ? decodeURIComponent(id) : 'none', skip: isNew}, {fetchPolicy: 'network-only'});
    const existing = data.node && 'name' in data.node ? data.node : null;

    const [create, creating] = useMutation<OpsTalentEditPageCreateMutation>(CREATE);
    const [update, updating] = useMutation<OpsTalentEditPageUpdateMutation>(UPDATE);
    const [destroy, deleting] = useMutation<OpsTalentEditPageDeleteMutation>(DELETE);

    const [form, setForm] = useState<Form>(EMPTY);
    const [errors, setErrors] = useState<string[]>([]);
    const [confirmingDelete, setConfirmingDelete] = useState(false);

    useEffect(() => {
        if (!existing) return;
        setForm({
            name: existing.name,
            slug: existing.slug,
            roles: [...existing.roles],
            headline: existing.headline ?? '',
            bio: existing.bio ?? '',
            skills: existing.skills.join(', '),
            dayRate: existing.dayRate ?? '',
            hourRate: existing.hourRate ?? '',
            imageUrl: existing.imageUrl ?? '',
            availability: existing.availability,
            published: existing.published,
            featured: existing.featured,
            position: String(existing.position),
            portfolio: existing.portfolioLinks.map((l) => `${l.label} | ${l.url}`).join('\n'),
            facebookProfileUrl: existing.facebookProfileUrl ?? '',
            facebookPageUrl: existing.facebookPageUrl ?? '',
        });
    }, [existing?.id]);

    if (!isNew && !existing) {
        return <p className="text-gray-500">Profile not found. <Link to="/ops/talent" className="text-lumina-purple">Back to talent</Link></p>;
    }

    const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm((f) => ({...f, [key]: value}));
    const toggleRole = (role: string) =>
        set('roles', form.roles.includes(role) ? form.roles.filter((r) => r !== role) : [...form.roles, role]);

    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        const dayRate = parseRate(form.dayRate);
        const hourRate = parseRate(form.hourRate);
        if (dayRate === null || hourRate === null) {
            setErrors(['Rates must be numbers']);
            return;
        }
        const input = {
            name: form.name.trim(),
            slug: form.slug.trim() || null,
            roles: form.roles,
            headline: form.headline.trim() || null,
            bio: form.bio.trim() || null,
            skills: form.skills.split(',').map((s) => s.trim()).filter(Boolean),
            dayRate,
            hourRate,
            imageUrl: form.imageUrl.trim() || null,
            availability: form.availability,
            published: form.published,
            featured: form.featured,
            position: Number(form.position) || 0,
            portfolioLinks: parseLinks(form.portfolio),
            facebookProfileUrl: form.facebookProfileUrl.trim() || null,
            facebookPageUrl: form.facebookPageUrl.trim() || null,
        };
        const onError = (err: Error) => setErrors([err.message]);
        if (isNew) {
            create({
                variables: {input: input as any},
                onCompleted: (res) => {
                    const p = res.createTalentProfile;
                    if (p?.talentProfile) navigate('/ops/talent');
                    else setErrors(p?.userErrors.map((u) => u.message) ?? ['Something went wrong']);
                },
                onError,
            });
        } else {
            update({
                variables: {input: {talentProfileId: existing!.id, ...input} as any},
                onCompleted: (res) => {
                    const p = res.updateTalentProfile;
                    if (p?.talentProfile) navigate('/ops/talent');
                    else setErrors(p?.userErrors.map((u) => u.message) ?? ['Something went wrong']);
                },
                onError,
            });
        }
    };

    const remove = () => {
        if (!existing) return;
        destroy({
            variables: {input: {talentProfileId: existing.id}},
            onCompleted: (res) => {
                const p = res.deleteTalentProfile;
                if (p?.deletedId) navigate('/ops/talent');
                else setErrors(p?.userErrors.map((u) => u.message) ?? ['Something went wrong']);
            },
            onError: (err) => setErrors([err.message]),
        });
    };

    const disconnect = () => {
        if (!existing) return;
        update({
            variables: {input: {talentProfileId: existing.id, disconnectUser: true}},
            onCompleted: (res) => {
                const p = res.updateTalentProfile;
                if (p?.talentProfile) window.location.reload();
                else setErrors(p?.userErrors.map((u) => u.message) ?? ['Something went wrong']);
            },
            onError: (err) => setErrors([err.message]),
        });
    };

    const busy = creating || updating || deleting;

    return (
        <div className="max-w-3xl">
            <Link to="/ops/talent" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-white mb-4">
                <ArrowLeft className="w-3 h-3"/> Talent
            </Link>
            <h1 className="text-3xl font-display font-bold mb-6">{isNew ? 'New profile' : existing!.name}</h1>

            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block">
                    <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Name</span>
                    <input value={form.name} onChange={(e) => set('name', e.target.value)} required autoFocus className={`${inputClass} w-full`}/>
                </label>
                <label className="block">
                    <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Slug (blank = from name)</span>
                    <input value={form.slug} onChange={(e) => set('slug', e.target.value)} placeholder="sarah-jenkins" className={`${inputClass} w-full font-mono`}/>
                </label>

                <fieldset className="md:col-span-2">
                    <legend className="text-xs uppercase tracking-wider text-gray-500 mb-2">Roles</legend>
                    <div className="flex flex-wrap gap-3">
                        {ROLES.map((role) => (
                            <label key={role} className={`text-xs px-3 py-1 border cursor-pointer select-none ${form.roles.includes(role) ? 'border-lumina-accent text-white' : 'border-gray-700 text-gray-400'}`}>
                                <input type="checkbox" className="sr-only" checked={form.roles.includes(role)} onChange={() => toggleRole(role)}/>
                                {role.replace(/_/g, ' ').toLowerCase()}
                            </label>
                        ))}
                    </div>
                </fieldset>

                <label className="block md:col-span-2">
                    <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Headline</span>
                    <input value={form.headline} onChange={(e) => set('headline', e.target.value)} placeholder="e.g. Documentary DP, Sony FX ecosystem" className={`${inputClass} w-full`}/>
                </label>
                <label className="block md:col-span-2">
                    <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Bio</span>
                    <textarea value={form.bio} onChange={(e) => set('bio', e.target.value)} rows={4} className={`${inputClass} w-full`}/>
                </label>
                <label className="block md:col-span-2">
                    <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Skills (comma separated)</span>
                    <input value={form.skills} onChange={(e) => set('skills', e.target.value)} placeholder="Lighting, Gimbal, DaVinci Resolve" className={`${inputClass} w-full`}/>
                </label>

                <label className="block">
                    <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Day rate ($)</span>
                    <input value={form.dayRate} onChange={(e) => set('dayRate', e.target.value)} inputMode="decimal" className={`${inputClass} w-full`}/>
                </label>
                <label className="block">
                    <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Hourly rate ($)</span>
                    <input value={form.hourRate} onChange={(e) => set('hourRate', e.target.value)} inputMode="decimal" className={`${inputClass} w-full`}/>
                </label>

                <label className="block md:col-span-2">
                    <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Portfolio links (one per line: Label | URL, or just the URL)</span>
                    <textarea value={form.portfolio} onChange={(e) => set('portfolio', e.target.value)} rows={3} placeholder={'Reel | https://vimeo.com/...\nhttps://instagram.com/...'} className={`${inputClass} w-full font-mono text-xs`}/>
                </label>
                <label className="block">
                    <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Facebook profile URL</span>
                    <input value={form.facebookProfileUrl} onChange={(e) => set('facebookProfileUrl', e.target.value)} placeholder="https://www.facebook.com/…" className={`${inputClass} w-full`}/>
                </label>
                <label className="block">
                    <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">
                        Facebook Page URL{existing?.facebookPageVerified && <span className="text-lumina-accent normal-case"> · verified{existing.facebookPageName ? `: ${existing.facebookPageName}` : ''}</span>}
                    </span>
                    <input value={form.facebookPageUrl} onChange={(e) => set('facebookPageUrl', e.target.value)} placeholder="https://www.facebook.com/…" className={`${inputClass} w-full`}/>
                </label>
                {existing && <FacebookConnection profile={existing} onDisconnect={disconnect} busy={creating || updating || deleting}/>}

                <label className="block md:col-span-2">
                    <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Portrait image URL</span>
                    <input value={form.imageUrl} onChange={(e) => set('imageUrl', e.target.value)} placeholder="https://…" className={`${inputClass} w-full`}/>
                </label>

                <label className="block">
                    <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Availability</span>
                    <select value={form.availability} onChange={(e) => set('availability', e.target.value)} className={`${inputClass} w-full`}>
                        {AVAILABILITIES.map((a) => <option key={a} value={a}>{a.toLowerCase()}</option>)}
                    </select>
                </label>
                <label className="block">
                    <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Sort position</span>
                    <input value={form.position} onChange={(e) => set('position', e.target.value)} inputMode="numeric" className={`${inputClass} w-full`}/>
                </label>

                <div className="flex gap-6 md:col-span-2">
                    <label className="flex items-center gap-2 text-sm text-gray-300">
                        <input type="checkbox" checked={form.published} onChange={(e) => set('published', e.target.checked)}/> Published on the site
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-300">
                        <input type="checkbox" checked={form.featured} onChange={(e) => set('featured', e.target.checked)}/> Featured (listed first)
                    </label>
                </div>

                {errors.length > 0 && (
                    <ul className="text-sm text-red-400 md:col-span-2">{errors.map((m) => <li key={m}>{m}</li>)}</ul>
                )}

                <div className="flex items-center gap-3 md:col-span-2">
                    <button type="submit" disabled={busy} className={primaryButton}>{isNew ? 'Create profile' : 'Save'}</button>
                    <Link to="/ops/talent" className={ghostButton}>Cancel</Link>
                    {!isNew && (
                        confirmingDelete ? (
                            <span className="ml-auto flex items-center gap-2 text-xs text-gray-400">
                                Delete this profile?
                                <button type="button" onClick={remove} disabled={busy} className={dangerButton}>Yes, delete</button>
                                <button type="button" onClick={() => setConfirmingDelete(false)} className={ghostButton}>No</button>
                            </span>
                        ) : (
                            <button type="button" onClick={() => setConfirmingDelete(true)} className={`${dangerButton} ml-auto`}>Delete</button>
                        )
                    )}
                </div>
            </form>
        </div>
    );
};

export default OpsTalentEditPage;
